/**
 * useTrackerHold — the FULL hold state of a tracker's Flow Optimizer, for
 * decisions (whether an Apply may write the draft first, what the drawer
 * status says). Tres Commas reads its scheduler status, whose blockedReason
 * already puts the global switch first; nothing is masked here.
 * `known` is false until that status has answered once (loading, or failing
 * before any answer) — callers must not treat that as "not held".
 * Voluum and Binom have no switch of their own: no request, known, not held.
 * Same query key and request as the Scheduler page.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useQuery } from '@tanstack/react-query';
import { matchTracker, type TrackerId } from '@trafficpulse/shared';

import { getSchedulerStatus } from '@/features/flow-optimizer/services/flow-optimizer-api';
import { optimizerCenterKeys } from '@/features/optimizer-center/hooks/useOptimizerConfigs';

export interface TrackerHold {
  /** The tracker's status has answered (always true without a switch). */
  known: boolean;
  /** Why Apply and the scheduler push nothing (global switch included), or null. */
  reason: string | null;
  /** The all-trackers switch is on (it has its own banner). */
  global: boolean;
}

const FREE: TrackerHold = { known: true, reason: null, global: false };

function hasOwnSwitch(id: TrackerId): boolean {
  return matchTracker(id, {
    voluum: () => false,
    binom: () => false,
    'tres-commas': () => true,
  });
}

export function useTrackerHold(id: TrackerId, enabled = true): TrackerHold {
  const ownSwitch = hasOwnSwitch(id);
  const { data } = useQuery({
    queryKey: optimizerCenterKeys.scheduler(id),
    queryFn: () => getSchedulerStatus(id),
    refetchInterval: 60_000,
    enabled: enabled && ownSwitch,
  });
  if (!ownSwitch) return FREE;
  return {
    known: data !== undefined,
    reason: data?.blockedReason ?? null,
    global: data?.globalKillSwitch === true,
  };
}
